import { CompositionConfig } from '../../../types/CompositionConfig';
import { TrackType } from '../../../types/Track/Type';
import { SegmentsTrack } from '../../../types/Track/SegmentsTrack';
import { Track } from '../../../types/Track/Track';
import { SegmentType } from '../../../types/Segment/Type';
import { CompositeSegment } from '../../../types/Segment/CompositeSegment';
import { ElementsTrack } from '../../../types/Track/ElementsTrack';
import { VideoElement } from '../../../types/Element/VideoElement';
import { ImageElement } from '../../../types/Element/ImageElement';
import { AudioElement } from '../../../types/Element/AudioElement';
import { ElementType } from '../../../types/Element/Type';
import { Effect } from '../../../types/Animations/Effects';
import { Scene } from '../../../types/Scene';
import { LottieElement } from '../../../types/Element/LottieElement';
import { TextElement } from '../../../types/Element/TextElement';
import { currentClip, saveClipChanges, updateClipMetadataWithId } from '@/stores/clip';
import { updateClipDataWithSplits } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtilsWithSplits/updateWithSplits';
import { CLIP_UPDATE_TYPE } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtilsWithSplits/constants';
import type { SanitizedTrim } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtilsWithSplits/types';
import { CaptionLineConfig, CaptionStyle, type Clip } from '@/domains/asset';
import { DEFAULT_FONT_SIZE } from '@/App/remotion/libs/caption/styles';
import { getCaptionPreset } from '@/App/remotion/libs/caption/caption-presets/utils';
import { getCaptionHighlightColor } from '@/adapters/aiEdits/utils/aiEditsProcessor';
import { mergeDeletes } from '@/components/molecules/Transcript/EditTranscriptMenu/utils';
import {
  insertElementIntoNewNonOverlappingTrack,
  updatePlayerConfigWithDeletes
} from '@/Pages/Clip/ClipPlayer/ClipAssets/utils';
import { getClipFPS } from '@/libs/clipUtils';
import { computeCaptionsForClip } from '@/App/remotion/libs/caption/caption';

function mergeTrimsWithExistingDeletes(
  existingDeletes: Record<number, any>,
  trims: Array<{ bounds: [number, number]; prev_end_time: number; next_start_time: number }>
): Record<number, any> {
  const newDeletes = trims.reduce(
    (acc, trim) => {
      acc[trim.bounds[0]] = trim;
      return acc;
    },
    { ...existingDeletes }
  );
  return mergeDeletes(newDeletes);
}

/**
 * Adjusts clip start/end boundaries based on trims that overlap with boundaries
 */
function adjustClipBoundariesForTrims(
  originalStart: number,
  originalEnd: number,
  trims: Array<{ bounds: [number, number]; prev_end_time: number; next_start_time: number }>
): { adjustedStart: number; adjustedEnd: number } {
  let adjustedStart = originalStart;
  let adjustedEnd = originalEnd;

  trims.forEach(trim => {
    if (trim.bounds[0] <= originalStart && trim.bounds[1] > originalStart) {
      adjustedStart = trim.bounds[1];
    }
    if (trim.bounds[1] >= originalEnd && trim.bounds[0] < originalEnd) {
      adjustedEnd = trim.bounds[0];
    }
  });

  return { adjustedStart, adjustedEnd };
}

function processDeletesAndUpdateClip(
  clipId: string,
  trims: Array<{ bounds: [number, number]; prev_end_time: number; next_start_time: number }>
) {
  // NOTE: This getSnapshot() call intentionally reads the clip state that was written by the
  // updateClipMetadataWithId call in Step 1 of updateAllElementsWithAIEdits.
  const updatedClip = currentClip.getSnapshot()[clipId];
  const updatedClipMetadata = updatedClip.asset_metadata;

  const mergedDeletes = mergeTrimsWithExistingDeletes(updatedClipMetadata.deletes || {}, trims);

  const { adjustedStart, adjustedEnd } = adjustClipBoundariesForTrims(
    updatedClipMetadata.start,
    updatedClipMetadata.end,
    trims
  );

  const isBoundaryTrim = adjustedStart !== updatedClipMetadata.start || adjustedEnd !== updatedClipMetadata.end;

  const clipWithDeletesAndBoundaries: Clip = {
    ...updatedClip,
    asset_metadata: {
      ...updatedClipMetadata,
      deletes: mergedDeletes,
      start: adjustedStart,
      end: adjustedEnd
    }
  };

  const captionsData = computeCaptionsForClip(clipWithDeletesAndBoundaries);

  const metadataUpdate: Partial<Clip['asset_metadata']> = {
    deletes: mergedDeletes,
    start: adjustedStart,
    end: adjustedEnd,
    captions: captionsData.captions,
    excludes: captionsData.excludes,
    duration: captionsData.duration
  };

  if (isBoundaryTrim) {
    // For boundary trims, config.ts skips element adjustment (start/end guard fires).
    // Manually adjust elements here using new deletes but same start/end so the
    // updatePlayerConfigWithDeletes guard does not skip adjustment.
    const fps = getClipFPS(updatedClip);
    const updatedPlayerConfig = JSON.parse(JSON.stringify(updatedClipMetadata.player_config)) as CompositionConfig;

    const allElementTracks = updatedPlayerConfig.playerConfig!.tracks.filter(
      track => track.type === TrackType.ELEMENTS
    ) as ElementsTrack[];
    const allNonElementTracks = updatedPlayerConfig.playerConfig!.tracks.filter(
      track => track.type !== TrackType.ELEMENTS
    ) as Track[];

    // Pass same start/end as prevClip so the guard inside updatePlayerConfigWithDeletes
    // does not bail out, ensuring adjustElementsWithDeletes runs exactly once.
    const clipWithNewDeletesOnly: Clip = {
      ...updatedClip,
      asset_metadata: {
        ...updatedClipMetadata,
        deletes: mergedDeletes
      }
    };

    const adjustedElementTracks = updatePlayerConfigWithDeletes(
      allElementTracks,
      fps,
      updatedClip,
      clipWithNewDeletesOnly
    );

    updatedPlayerConfig.playerConfig!.tracks = [...allNonElementTracks, ...adjustedElementTracks];
    metadataUpdate.player_config = updatedPlayerConfig;
  }
  // For internal trims (start/end unchanged) we intentionally do NOT save player_config.
  // config.ts will run updatePlayerConfigWithDeletes exactly once via getFinalClipWithRemotionConfig.

  updateClipMetadataWithId(clipId, metadataUpdate);
}

export function updateAllElementsWithAIEdits(
  clipId: string,
  elements: (VideoElement | ImageElement | AudioElement | LottieElement | TextElement)[] | undefined,
  effects?: Effect[],
  sceneChanges?: Scene[],
  trims?: Array<{ bounds: [number, number]; prev_end_time: number; next_start_time: number }>,
  skipSave?: boolean
) {
  const clip = currentClip.getSnapshot()[clipId];
  const clipMetadata = clip.asset_metadata;

  const defaultLineConfig = CaptionLineConfig.OneLine;

  const playerConfig = JSON.parse(JSON.stringify(clip.asset_metadata.player_config)) as CompositionConfig;
  if (!playerConfig) {
    return;
  }

  // Only process elements/tracks if elements is explicitly provided (not undefined)
  if (elements !== undefined) {
    const allElementIdsSentByAIEdits = elements.map(element => element.id);

    const allExistingTracks = playerConfig?.playerConfig?.tracks || [];
    const allExistingNonElementsTracks = allExistingTracks.filter(
      track => track.type !== TrackType.ELEMENTS
    ) as Track[];
    const allExistingElementTracks = allExistingTracks.filter(
      track => track.type === TrackType.ELEMENTS
    ) as ElementsTrack[];

    const filteredElementsTracksWithRetainedElements = allExistingElementTracks
      .map(track => ({
        ...track,
        elements: track.elements.filter(
          element =>
            allElementIdsSentByAIEdits.includes(element.id) ||
            ![ElementType.VIDEO, ElementType.TEXT, ElementType.AUDIO].includes(element.type)
        )
      }))
      .filter(track => track.elements.length > 0);

    const existingElementIds = allExistingElementTracks.flatMap(track => track.elements.map(element => element.id));

    const newElements = elements.filter(element => !existingElementIds.includes(element.id));

    // Distribute new elements into non-overlapping tracks using the existing utility
    let updatedElementTracks = [...filteredElementsTracksWithRetainedElements];

    for (const element of newElements) {
      updatedElementTracks = insertElementIntoNewNonOverlappingTrack(updatedElementTracks, element);
    }

    playerConfig.playerConfig!.tracks = [...allExistingNonElementsTracks, ...updatedElementTracks];
  }

  const compositeSegment = (
    playerConfig.playerConfig.tracks.find(track => track.type === TrackType.SEGMENTS) as SegmentsTrack
  )?.segments.find(segment => segment.type === SegmentType.COMPOSITE) as CompositeSegment;

  if (compositeSegment) {
    if (effects?.length) {
      compositeSegment.effects = effects;
    }
  }

  const suitableBrandkitColor = getCaptionHighlightColor();
  const siliconPreset = getCaptionPreset('silicon');

  const customSiliconPreset = {
    ...siliconPreset,
    wordStyle: {
      ...siliconPreset.wordStyle,
      highlightBoxStyle: {
        ...siliconPreset.wordStyle?.highlightBoxStyle,
        background: suitableBrandkitColor
      }
    }
  };

  const updatedCaptionDetails =
    !clipMetadata.subtitle || clipMetadata.layout === 'AUDIOGRAM'
      ? {
          caption_styles: {
            scale: 1,
            font_size: DEFAULT_FONT_SIZE,
            caps: false,
            line_config: clipMetadata.caption_styles?.line_config || defaultLineConfig,
            animation: clipMetadata.caption_styles?.animation || CaptionStyle.Outline,
            ...clipMetadata.caption_styles,
            ...customSiliconPreset,
            pageStyle: {
              ...clipMetadata.caption_styles?.pageStyle,
              textShadow: '4px 4px 6px rgb(0, 0, 0)'
            }
          },
          caption_positions: {
            x: 2.5,
            y: 82,
            width: 95,
            height: 18
          },
          layout: clipMetadata.layout === 'AUDIOGRAM' ? 'SPEAKER' : clipMetadata.layout,
          subtitle: true
        }
      : {};

  // Step 1: First update clip with elements, effects, and scenes
  updateClipMetadataWithId(clipId, {
    ...updatedCaptionDetails,
    magicLayout: {
      ...(clipMetadata.magicLayout as any),
      showSpeakerLabels: false
    },
    player_config: playerConfig as any,
    ...(sceneChanges?.length && { scenes: sceneChanges })
  });

  // Step 2: Process deletes if present
  if (trims && trims.length > 0) {
    processDeletesAndUpdateClip(clipId, trims);
  }

  if (!skipSave) {
    saveClipChanges({ clipId, skipRemotionUpdate: true });
  }
}

export function updateAllElementsWithAIEditsAndSplits(
  clipId: string,
  elements: (VideoElement | ImageElement | AudioElement | LottieElement | TextElement)[],
  compositeScenes: Array<{ compositeId: string; scenes: Scene[] }>,
  compositeEffects: Array<{ compositeId: string; effects: Effect[] }>,
  compositeDeletes: Array<{ compositeId: string; trims: SanitizedTrim[] }>
) {
  // Step A: Apply elements + caption preset to player_config

  updateAllElementsWithAIEdits(clipId, elements, [], [], [], true);

  const clipAfterElements = currentClip.getSnapshot()[clipId];

  // Step B: Set scenes per split (skip post-processing)
  const clipB = updateClipDataWithSplits(
    { clipId, actionType: CLIP_UPDATE_TYPE.BULK_UPDATE_COMPOSITE_SEGMENT_SCENES, compositeScenes },
    { skipPostProcessing: true, currentClipData: clipAfterElements }
  );

  // Step C: Set effects per split (skip post-processing)
  const clipC = updateClipDataWithSplits(
    { clipId, actionType: CLIP_UPDATE_TYPE.BULK_UPDATE_COMPOSITE_SEGMENT_EFFECTS, compositeEffects },
    { skipPostProcessing: true, currentClipData: clipB }
  );

  // Step D: Add deletes per split — runs full post-processing + playerConfig rebuild
  // Always called (even with empty deletes) to ensure post-processing runs after Steps B and C

  updateClipDataWithSplits(
    { clipId, actionType: CLIP_UPDATE_TYPE.BULK_UPDATE_COMPOSITE_SEGMENT_DELETES, compositeDeletes },
    { currentClipData: clipC }
  );
}
